import { useMemo, useState } from "react";

import type { WhatsNewSourceI } from "@/modules/whats-new/data/use-whats-new-cards";
import { resolveWhatsNewEntry } from "@/modules/whats-new/lib/audience";
import {
  WHATS_NEW_AUDIENCES,
  type WhatsNewAudienceT,
} from "@/modules/whats-new/lib/types";

import { useFetchDailyTodos, useFetchDailyTodosSwitch } from "../data/queries";
import { DAILY_TODOS_2026_10_27 } from "./entry";

const isManagerAudience = (audience: WhatsNewAudienceT | null) =>
  audience === WHATS_NEW_AUDIENCES.LGA ||
  audience === WHATS_NEW_AUDIENCES.ADMIN;

const isInWindow = (now: Date) =>
  now >= new Date(DAILY_TODOS_2026_10_27.startsAt) &&
  now < new Date(DAILY_TODOS_2026_10_27.endsAt);

/**
 * The To-Dos announcement, only while the backend switch is on. Unknown
 * (`isSwitchedOn` undefined while it loads) isn't settled; off, failed or
 * not for this viewer settles with no card, so it never holds the other
 * sources back.
 */
export const resolveDailyTodosWhatsNewSource = (
  audience: WhatsNewAudienceT | null,
  isSwitchedOn: boolean | undefined
): WhatsNewSourceI => {
  if (!audience || isSwitchedOn === undefined) {
    return { cards: [], isSettled: false };
  }

  const card = isSwitchedOn
    ? resolveWhatsNewEntry(DAILY_TODOS_2026_10_27, audience)
    : null;

  return { cards: card ? [card] : [], isSettled: true };
};

/**
 * Viewers with To-Dos (`hasDailyTodos`, from useDailyTodosViewer in the What's
 * new module) learn the switch from their To-Dos query, shared with the nav,
 * so no extra request. Other callers (not approved on a campaign) get no card
 * and no request. LGAs and Admins, who have no To-Dos, ask once per load and
 * only inside the entry's window. Kept free of the global context and Auth0,
 * like every What's new source.
 */
export const useDailyTodosWhatsNewCards = (
  audience: WhatsNewAudienceT | null,
  hasDailyTodos: boolean | undefined
): WhatsNewSourceI => {
  const [now] = useState(() => new Date());
  const isManager = isManagerAudience(audience);
  const askSwitch = isManager && isInWindow(now);
  const callerQuery = useFetchDailyTodos(
    !!audience && !isManager && hasDailyTodos === true
  );
  const switchQuery = useFetchDailyTodosSwitch(askSwitch);

  return useMemo(() => {
    if (!audience) {
      return { cards: [], isSettled: false };
    }

    if (isManager) {
      if (!askSwitch || switchQuery.isError) {
        return resolveDailyTodosWhatsNewSource(audience, false);
      }

      return resolveDailyTodosWhatsNewSource(audience, switchQuery.data);
    }

    if (hasDailyTodos === undefined) {
      return resolveDailyTodosWhatsNewSource(audience, undefined);
    }

    if (!hasDailyTodos || callerQuery.isError) {
      return resolveDailyTodosWhatsNewSource(audience, false);
    }

    return resolveDailyTodosWhatsNewSource(
      audience,
      callerQuery.data?.isSwitchedOn
    );
  }, [
    askSwitch,
    audience,
    callerQuery.data?.isSwitchedOn,
    callerQuery.isError,
    hasDailyTodos,
    isManager,
    switchQuery.data,
    switchQuery.isError,
  ]);
};
